import { Component, OnInit, ViewChild, ElementRef, ViewEncapsulation } from '@angular/core';
import { LoginComponent, LoginInfo } from './login/login.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { MatMenuTrigger } from '@angular/material/menu';
import { DataEditorComponent } from './data-editor/data-editor.component';
import { ContextService, ServerServiceConfig, LoginData, LayoutInterface } from './context.service';
import { Router, NavigationStart, ActivatedRoute } from '@angular/router';
import { ServerLoginService } from './server-login.service';

import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { MatToolbar } from '@angular/material/toolbar';
import { MatSnackBar } from '@angular/material/snack-bar';
import tinycolor from 'tinycolor2';

/** This is the welcome page that shows when we are not logged in and may not have connected */

@Component({
  selector: 'mutara-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class AppComponent implements OnInit {
  favIcon: HTMLLinkElement | null = document.querySelector("#appFavIcon");
  favIcon16: HTMLLinkElement | null = document.querySelector("#appFavIcon16");
  favIcon32: HTMLLinkElement | null = document.querySelector("#appFavIcon32");
  appTitle: HTMLTitleElement | null = document.querySelector("#appTitle");
  @ViewChild(MatToolbar) toolbar: MatToolbar | undefined;
  @ViewChild("navTrigger") accountNav: MatMenuTrigger | undefined;
  /*
    context = {
      chat: []
    }
    chatTimer: any;
    */
  today: number = Date.now();
  progress: number = 0;
  message: string | null = null;

  // This is the user data
  loginData: LoginData | undefined;
  appConfig: ServerServiceConfig | undefined;

  layouts: LayoutInterface[] = [];

  isEmbed: boolean = false;
  showProgress: boolean = false;

  pageTitle: string | null = null;
  configDesc: string | null = null;
  isVertical: boolean = false;
  editorVisible: boolean = false;

  // Dialogs login and chat
  private loginDialog: MatDialogRef<LoginComponent> | undefined | null;
  private editorDialog: MatDialogRef<DataEditorComponent> | undefined | null;

  private colorMap: any = {
    '50': { dark: false, value: 12 },
    '100': { dark: false, value: 30 },
    '200': { dark: false, value: 50 },
    '300': { dark: false, value: 70 },
    '400': { dark: false, value: 85 },
    '500': { dark: false, value: 100 },
    '600': { dark: true, value: 87 },
    '700': { dark: true, value: 70 },
    '800': { dark: true, value: 54 },
    '900': { dark: true, value: 25 },
    'A100': { dark: true, value: 15, saturation: 80, light: 65 },
    'A200': { dark: true, value: 15, saturation: 80, light: 55 },
    'A400': { dark: true, value: 15, saturation: 100, light: 45 },
    'A700': { dark: true, value: 15, saturation: 100, light: 40 }
  };

  constructor(
    private httpConnection: HttpClient,
    public router: Router,
    private actRoute: ActivatedRoute,
    private contextSettings: ContextService,
    private element: ElementRef,
    private dialog: MatDialog,
    private motd: MatSnackBar) {

    // This is where we load up the theme color palettes and setup instructions etc.
    this.contextSettings.subscribeTo("applicationInfo", (config: ServerServiceConfig) => {
      if (config === null) {
        console.error('No config yet!! Are we connecting to the remote services or have they crashed?');
        return;
      }
      if (this.appConfig === undefined || !(this.appConfig.serviceVersion == config.serviceVersion && this.appConfig.serviceUrl == config.serviceUrl)) {
        if (!this.appConfig?.production) {
          this.manageFakeSubmit();
        }
        this.appConfig = config;
        this.configDesc = config?.description;

        this.element.nativeElement.style.setProperty('--registration-image', `url(${this.appConfig?.registrationImage})`);
        this.element.nativeElement.style.setProperty('--toolbar-background', `url(/assets/${this.appConfig?.themeDir}/toolbar-background.png)`);
        /// The following values are from the index.html wrapper of the app and need the reset once the config is loaded
        if (!!this.appTitle) {
          this.appTitle.text = this.getPageTitle();
        }
        if (!!this.favIcon) {
          this.favIcon.href = `/assets/${config.themeDir}/favicon.ico`;
        }
        this.buildColorMaps(config);
        /*
        this.favIcon16.href = `/assets/${config.themeDir}/favicon-16x16.png`;
        this.favIcon32.href = `/assets/${config.themeDir}/favicon-32x32.png`;
        */

        this.contextSettings.loadPropertyVariables(config.theme, this.element.nativeElement.style);
      }
    });
  }

  /**
   * Here we load from local storage to allow this user to reconnect to the service server
   */
  ngOnInit() {
    this.actRoute.queryParams.subscribe(params => {
      this.isEmbed = (params.embedded == 'true');
    });

    this.httpConnection.get('$service/motd').subscribe((result: any) => {
      if (!!result && result.length > 0) {
        this.message = result.reduce((acc: any, messageObj: any) => `${acc} ${messageObj.message}\n`, '');
        this.showMOTD();
      }
    });

    // This is to catch any application changes (currently doesn't happen except once)
    this.contextSettings.subscribeTo("loginRequest", (popup: boolean) => {
      if (popup) {
        this.createLoginDialog();
      }
    });
    // This is the receiver for the login data
    this.contextSettings.subscribeTo("loginData", (login: LoginData) => {
      this.loginData = login;
    
      // BMS TODO: Force the navigation to present the list of applications 
      this.contextSettings.setLayoutId(login?.defaultLayout);
    });

    this.contextSettings.subscribeTo("showProgressValue", (visible: boolean) => {
      setTimeout(() => {
        this.showProgress = visible;
      }); // Make this async to prevent error
    });

    this.contextSettings.subscribeTo("layoutIdentity", (layout: LayoutInterface) => {
      this.pageTitle = this.contextSettings.expandVariables(layout?.layoutKey ?? this.configDesc, this.appConfig?.info);
      if (!!this.appTitle) {
        this.appTitle.text = this.getPageTitle();
      }
    });

    this.contextSettings.subscribeTo("orientation", (isVertical : boolean) => {
      this.isVertical = isVertical;
    });
  }

  /// Derive the colors for the palette variables..... Angular2 
  /// TBD, convert to Angular 12 scss style sheets. 
  __colorAdjust(tc: | any) {
    return new tinycolor({
      r: Math.floor(Math.pow(tc._r, 2) / 255),
      b: Math.floor(Math.pow(tc._b, 2) / 255),
      g: Math.floor(Math.pow(tc._g, 2) / 255)
    });
  }

  manageFakeSubmit() {
    this.contextSettings.addNewMessage('FAKESUBMIT', false);
    this.contextSettings.subscribeTo('FAKESUBMIT', (testSubmit: boolean) => {
      if (testSubmit && this.isLoggedIn) {
        this.contextSettings.nextMessage('formComplete', true);
      }
    });
  }

  get isLoggedIn() {
    return this.loginData && (this.loginData?.username ?? '') != '';
  }

  buildColorMaps(config: any) {
    ['primary', 'accent', 'warn', 'success', 'neutral', 'indeterminate'].forEach(type => {
      try {
        let name = `--${type}-color`, value = config.theme[name];
        if (!!value) {
          let color = new tinycolor(value),
            light = new tinycolor('#ffffff'),
            black = new tinycolor('#000000'),
            dark = new tinycolor(this.__colorAdjust(color)),
            triad = color.tetrad()[3];
          // Walk the map and build the full color profile as used in the application
          Object.keys(this.colorMap).forEach(level => {
            if (!config.theme[`--${type}-${level}`]) {
              let defn = this.colorMap[level],
                newColor = tinycolor.mix(defn.dark ? dark : light,
                  !defn.saturation ? color : triad,
                  defn.value);//.saturate(defn.saturation ?? 100).lighten(defn.light ?? 0);
              if (!!defn.saturation) {
                newColor = newColor.saturate(defn.saturation);
              }
              if (!!defn.light) {
                newColor = newColor.lighten(defn.light);
              }

              config.theme[`--${type}-${level}`] = '#' + newColor.toHex();
              config.theme[`--${type.substring(0, 1)}-contrast-${level}`] = '#' + (newColor.isDark() ? light : black).toHex();
            }
          });
        }
      } catch (err) {
        console.log('No Color Maps for %O', config.description);
      }
    });

  }

  get currentRoute(): string {
    return this.router.url.split('/')[1];
  }

  /**
   * opens a login dialog and sets up the processing subscription
   */
  createLoginDialog() {
    if (!this.loginDialog) {
      this.loginDialog = this.dialog.open(LoginComponent,
        {
          disableClose: false,
          autoFocus: true,
          // panelClass: 'custom-dialog',
          width: 'auto',
          height: 'auto',
          hasBackdrop: true,
          data: {},
          role: 'dialog',
          ariaLabelledBy: 'modal-title',
          id: 'modal-container-' + Date.now()
        });
      this.loginDialog.componentInstance.roleOptions.subscribe({
        next: (options: any[]) => {
          this.layouts = options;
          /*
          if ( loginData?.layout ) {
            this.layouts.push({ value: 'editor', label: 'Form Editor', icon: 'edit' });
          }
          */
        }
      });

      this.loginDialog.afterClosed().subscribe({
        next: data => {
          setTimeout(() => this.processLogin(data));
        }
      });
    }
  }
  /**
   * Send the data to the server for authentication. If successful, we recieve context and user information
   * @param loginData
   */
  processLogin(loginData: any) {
    if (this.loginDialog) {
      this.loginDialog = null;
    }
    if (!!loginData?.user && !!loginData?.pwd) {
      // here we have several options, 
      // Login, Login with Role, Change Password
      let service = '$service';
      if (!!loginData.forgotten) { // Change Password
        service = '$service/change-password';
      }
      this.httpConnection.post(service, loginData).subscribe({
        error: err => {
          console.error(err.message);
          this.createLoginDialog();
          return err;
        },
        next: (data: any) => {
          if (data.type == 0) {
            this.layouts = [];
          }
        }
      });
    }
  }


  logoutUser() {
    this.accountNav?.closeMenu();
    this.contextSettings.nextMessage('logout', true);
    this.pageTitle = null;
    if (!!this.appTitle) { this.appTitle.text = this.getPageTitle(); }

  }

  login($event: Event) {
    if (!!this.loginData && !this.loginData.username && !!this.accountNav) {
      $event.preventDefault();
      this.accountNav?.closeMenu();
      this.createLoginDialog();
    }
    return !this.loginData?.username;
  }

  getPageTitle(): string {
    return this.pageTitle ?? this.configDesc ?? 'Mutara Application';
  }

  showMOTD() {
    if (!!this.message) {
      this.motd.open(this.message, 'OK');
      setTimeout(() => this.motd.dismiss(), 30000);
    }
  }

  /** TBD THIS IS NOT FUNCTIONAL - BMS */
  popupEditor() {
    //   this.contextSettings.setInstructions(taxnonmyLink);
    this.editorDialog = this.dialog.open(DataEditorComponent,
      {
        width: '640px',
        height: '480px',
        // panelClass: 'custom-dialog',
        position: {
          top: '1em',
          left: '1em'
        },
        hasBackdrop: false
      });
    this.editorDialog.afterClosed().subscribe(() => {
      // this.contextSettings.setInstructions(this.instructionLink);
      // Nothing is passed, but we need to submit changes
      console.log('Save here');
    })
  }
  /* switch layout */
  switchLayout(layout: any) {
    switch (layout.value) {
      case 'editor':
        this.contextSettings.activateEditor((layout.active = !layout.active));
        break;
      case 'reload':
        this.contextSettings.reloadServices(ServerLoginService.configService);
        break;
      default:
        this.contextSettings.setLayoutId(this.contextSettings.findLayout(layout.value));
        break;
    }
  }
  /**
   * Recalc the deploy time
   */
  getDeployTime() {
    return !!this.appConfig?.serviceVersion ? new Date(parseInt((this.appConfig?.serviceVersion ?? '').split('.')?.pop() ?? '0') * 3600000).toString() : new Date(0);
  }
  dumpForm() {
    this.contextSettings.dumpFormData();
  }
  toggleEditor() {
    this.contextSettings.nextMessage('formEditor', this.editorVisible = !this.editorVisible);
  }
  fakeSubmit() {
    if (!!this.login) {
      this.contextSettings.nextMessage('FAKESUBMIT', true);
    }
  }
  skipToMainContentClick(event: Event) {
    event.preventDefault();
    let thisElem = document.getElementById("content") as HTMLAnchorElement;
    thisElem.focus();
    return;
  }

  get flairImage() {
    return '/assets/' + (this.appConfig?.themeDir ?? 'mutara') + (!!this.appConfig?.flairOverHome ? '/home-image.png' : '/flair.png');
  }
  get homeImage() {
    return '/assets/' + (this.appConfig?.themeDir ?? 'mutara') + (!!this.appConfig?.flairOverHome ? '/flair.png' : '/home-image.png');
  }
}